import React, { useEffect, useState, useCallback } from 'react';
import { FiX, FiBell, FiCheckCircle } from 'react-icons/fi';
import { getSocket } from '../services/socket';
import { AUTH_API_BASE_URL as API_BASE_URL } from '../LeadMng/config/api';

const typeTitle = (type) => {
  if (type === 'task') return 'New Task Assigned';
  if (type === 'ticket') return 'Support Ticket Assigned';
  if (type === 'project') return 'Added to Project';
  if (type === 'milestone') return 'Milestone Assigned';
  return 'New Lead Assigned';
};

const AssignmentNotificationPopup = () => {
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        const unread = (Array.isArray(data) ? data : []).filter(n => n.unread);
        setItems(unread.slice(0, 5));
        setVisible(unread.length > 0);
      }
    } catch (e) {
      // silent
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/notifications/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        setItems([]);
        setVisible(false);
      }
    } catch (e) {
      // silent
    }
  }, []);

  const dismissOne = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) return;
      await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (e) {
      // silent
    } finally {
      setItems(prev => {
        const next = prev.filter(i => i.id !== id);
        if (next.length === 0) setVisible(false);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const socket = getSocket();
    const onNew = (payload) => {
      setItems(prev => {
        const exists = prev.some(i => i.id === payload.id);
        const next = exists ? prev : [{ ...payload, unread: true }, ...prev].slice(0, 5);
        if (next.length > 0) setVisible(true);
        return next;
      });
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(typeTitle(payload.type), {
            body: payload.message || '',
            tag: payload.id
          });
        } catch {}
      }
    };
    socket.on('notification:new', onNew);
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    // Fallback polling to ensure fetch even if socket missed
    const interval = setInterval(fetchUnread, 20000);
    // Listen to app-level events to refetch immediately
    const onTaskCreated = () => fetchUnread();
    const onTaskShifted = () => fetchUnread();
    const onLeadAssigned = () => fetchUnread();
    const onTicketAssigned = () => fetchUnread();
    const onProjectAssigned = () => fetchUnread();
    const onMilestoneAssigned = () => fetchUnread();
    window.addEventListener('taskCreated', onTaskCreated);
    window.addEventListener('lead-assigned', onLeadAssigned);
    window.addEventListener('task-shifted', onTaskShifted);
    window.addEventListener('ticket-assigned', onTicketAssigned);
    window.addEventListener('project-assigned', onProjectAssigned);
    window.addEventListener('milestone-assigned', onMilestoneAssigned);
    return () => {
      socket.off('notification:new', onNew);
      clearInterval(interval);
      window.removeEventListener('taskCreated', onTaskCreated);
      window.removeEventListener('lead-assigned', onLeadAssigned);
      window.removeEventListener('task-shifted', onTaskShifted);
      window.removeEventListener('ticket-assigned', onTicketAssigned);
      window.removeEventListener('project-assigned', onProjectAssigned);
      window.removeEventListener('milestone-assigned', onMilestoneAssigned);
    };
  }, [fetchUnread]);

  if (!visible || items.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[2000] max-w-sm w-[22rem] space-y-3 pointer-events-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <FiBell className="text-indigo-600" />
            </div>
            <span className="font-semibold text-gray-800">Notifications</span>
            <span className="ml-1 text-xs px-2 py-0.5 bg-red-500 text-white rounded-full">{items.length}</span>
          </div>
          <button
            onClick={markAllRead}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
            title="Mark all as read"
          >
            <FiCheckCircle /> Clear
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.map(item => (
            <div key={item.id} className="px-4 py-3 border-b border-gray-50 last:border-b-0">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {typeTitle(item.type)}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {item.message}
                  </div>
                  {item.meta && item.meta.companyName && (
                    <div className="text-xs text-indigo-600 font-medium mt-0.5">
                      {item.meta.companyName}
                    </div>
                  )}
                  <div className="text-[10px] text-gray-400 mt-1">
                    {new Date(item.createdAt).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => dismissOne(item.id)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Dismiss"
                >
                  <FiX />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssignmentNotificationPopup;
